import { useState } from 'react'

export const HeroSection: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 mb-4">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              言葉で遊ぼう
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
              無料の4文字日本語Wordleゲーム。ひらがなの今日の単語を12回以内に当てよう。
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 px-3 py-1 rounded-full text-sm font-medium">
                4文字Wordle
              </span>
              <span className="inline-block bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 px-3 py-1 rounded-full text-sm font-medium">
                ひらがな単語
              </span>
              <span className="inline-block bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100 px-3 py-1 rounded-full text-sm font-medium">
                毎日更新
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium transition-colors"
        >
          <span>{isExpanded ? 'ゲーム情報を隠す' : 'ゲーム情報を表示'}</span>
          <svg
            className={`w-5 h-5 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </button>

        {isExpanded && (
          <div className="mt-6 space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  🎮 遊び方
                </h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• 今日の単語を12回以内に当てる</li>
                  <li>• ひらがな4文字の単語を入力</li>
                  <li>• 緑、黄、灰色のヒントで候補を絞る</li>
                  <li>• 日本時間0時に新しい問題へ切り替わる</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  📚 単語リスト
                </h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• 名詞、代名詞、形容詞、副詞、動詞</li>
                  <li>• 動詞は基本的に終止形で入力</li>
                  <li>• 外来語や日常語も候補に含む</li>
                  <li>• 日本語学習者でも読みやすいかな入力</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  ⚙️ ゲームモード
                </h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• 通常モード - まずは自由に候補を試す</li>
                  <li>• ハードモード - 判明した文字を必ず使う</li>
                  <li>• ヒントモード - かなの行や段の手掛かりを表示</li>
                  <li>• 高コントラストモード - アクセシビリティ向上</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  📚 練習の広げ方
                </h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• 4文字盤面で色ヒントの読み方を練習</li>
                  <li>• 5文字Wordleやことのはたんご前の準備にも便利</li>
                  <li>• 過去問カレンダーで復習できる</li>
                  <li>• スマホでも短時間で遊べる</li>
                </ul>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-800">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                ✨ 言葉で遊ぼうを選ぶ理由
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <li>✓ 無料で今日の4文字日本語Wordleをすぐ始められる</li>
                <li>✓ ローマ字入力をひらがなへ変換できる</li>
                <li>✓ ヒントモードで意味を確認しながら語彙を増やせる</li>
                <li>✓ 統計と連勝記録で毎日の挑戦を追跡できる</li>
                <li>✓ 4文字単語リストや攻略ガイドへ移動しやすい</li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                📊 学習への使い方
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <li>• 分からなかった単語の読み方と意味を調べる</li>
                <li>• 色ヒントの使い方を次の問題で試す</li>
                <li>• 過去問と統計を使って自分の記録を振り返る</li>
                <li>• ほかの教材と組み合わせて語彙を復習する</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
